import React from "react";
import { Phone, MapPin, Clock } from "lucide-react";
import { Button } from "../ui/button";

const WHATSAPP_LINK = "https://wa.me/919866679191?text=Hi%2C%20I%27m%20interested%20in%20your%20paint%20services";
const PHONE_LINK = "tel:+919866679191";

export const ContactSection = () => {
  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-28 contact-pattern" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-10 sm:mb-14">
          <span className="text-purple-600 font-semibold tracking-wider uppercase text-xs sm:text-sm">Get In Touch</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 sm:mt-3">
            Visit Our <span className="text-purple-600">Store</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="space-y-4 sm:space-y-5">
            <div className="flex gap-3 sm:gap-4 p-4 sm:p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Location</h4>
                <p className="text-gray-600 mt-1 text-xs sm:text-sm">
                  #21-2-158/3, Opp. Sidhartha School,<br />
                  Markandeya Colony, Godavarikhani,<br />
                  Telangana - 505209
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 p-4 sm:p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Contact</h4>
                <a href={PHONE_LINK} className="text-purple-600 font-semibold text-base sm:text-lg hover:underline">
                  +91 98666 79191
                </a>
                <p className="text-gray-500 text-xs mt-0.5">Contact phone</p>
              </div>
            </div>

            <div className="timing-card rounded-xl p-4 sm:p-5 shadow-sm">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Shop Timings</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">We are here to serve you</p>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-100">
                  <span className="font-medium text-gray-700 text-sm">Monday - Saturday</span>
                  <span className="font-bold text-purple-600 text-sm">9:00 AM – 8:30 PM</span>
                </div>
                <div className="flex justify-between items-center py-2 sm:py-3">
                  <span className="font-medium text-gray-700 text-sm">Sunday</span>
                  <span className="font-bold text-purple-600 text-sm">9:00 AM – 2:00 PM</span>
                </div>
              </div>
            </div>

            <Button asChild size="lg" className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 sm:py-6 rounded-xl text-base font-semibold shadow-md" data-testid="contact-whatsapp-button">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </Button>
          </div>

          {/* Map & Proprietor */}
          <div className="space-y-4 sm:space-y-5">
            {/* Google Maps */}
            <div className="rounded-xl overflow-hidden shadow-md" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3782.5!2d79.4833!3d18.7567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDQ1JzI0LjEiTiA3OcKwMjknMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sri Pavanasutha Traders Location"
                className="w-full"
              />
            </div>

            <a
              href="https://www.google.com/maps/search/Sri+Pavanasutha+Traders+Markandeya+Colony+Godavarikhani+505209"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 sm:py-4 bg-white border border-gray-200 hover:border-purple-400 rounded-xl text-purple-600 font-semibold transition-colors text-sm sm:text-base"
              data-testid="get-directions-button"
            >
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
              Get Directions on Google Maps
            </a>

            {/* Proprietor */}
            <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl p-5 sm:p-6 text-white">
              <p className="text-purple-200 text-xs uppercase tracking-wider mb-1">Proprietor</p>
              <h3 className="text-2xl sm:text-3xl font-bold">B. Anil Kumar</h3>
              <p className="text-purple-100 mt-3 text-sm leading-relaxed">
                We believe every wall tells a story. Let us help you paint yours with colors that inspire.
              </p>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs text-purple-200">Serving Godavarikhani since 2017</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
